import React from 'react';
import PropTypes from 'prop-types';
import { BrowserRouter } from 'react-router-dom';

import 'es-iterator-helpers/auto';
import { initializeSockets } from 'core/sockets';
import { NotificationsContainer, QueryNotifications } from 'core/notifications';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { withContainer } from 'core/cache';

import getSocketsConfig from '../helpers/getSocketsConfig';
import initAxiosConfig from '../helpers/initAxiosConfig';
import initI18n from '../helpers/initI18n';

import useGlobalQueries from '../hooks/useGlobalQueries';

import '@intellum/__ready';
import '@intellum/_a11y';

import 'uikit/utils/styles/reset.css';
import 'uikit/ids';
import '@/forms';
import 'uikit/forms';

const GlobalQueriesProvider = ({ children }) => {
  useGlobalQueries({ isEnabled: true });

  return children;
};

class AppContainer extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      isReady: false,
    };
    this.queryClient = new QueryClient({
      defaultOptions: {
        queries: {
          refetchOnWindowFocus: props.config.shouldRefetchOnWindowFocus,
        },
      },
    });
  }

  async componentDidMount() {
    const {
      i18nKey,
      i18nStrings,
      shouldConnectToSockets,
      shouldInitAxiosConfig,
      shouldLoadTranslations,
      socketsConfig,
    } = this.props.config;

    if (shouldInitAxiosConfig) initAxiosConfig();

    await Promise.all([
      initI18n({ key: i18nKey, shouldLoadTranslations, strings: i18nStrings }),
      shouldConnectToSockets &&
        new Promise((resolve) => {
          initializeSockets(resolve, socketsConfig || getSocketsConfig());
        }),
    ]);

    this.setState({ isReady: true });
  }

  render() {
    if (!this.state.isReady) return null;

    const { children } = this.props;

    return (
      <QueryClientProvider client={this.queryClient}>
        <GlobalQueriesProvider>
          <BrowserRouter future={{ v7_relativeSplatPath: true, v7_startTransition: true }}>
            <QueryNotifications />
            <NotificationsContainer />
            {children}
          </BrowserRouter>
        </GlobalQueriesProvider>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    );
  }
}

AppContainer.propTypes = {
  config: PropTypes.shape({
    i18nKey: PropTypes.string,
    i18nStrings: PropTypes.shape({}),
    shouldConnectToSockets: PropTypes.bool,
    shouldInitAxiosConfig: PropTypes.bool,
    shouldLoadTranslations: PropTypes.bool,
    socketsConfig: PropTypes.shape({}),
    shouldRefetchOnWindowFocus: PropTypes.bool,
  }),
};

AppContainer.defaultProps = {
  config: {
    i18nKey: '',
    i18nStrings: null,
    shouldConnectToSockets: false,
    shouldInitAxiosConfig: false,
    shouldLoadTranslations: false,
    shouldRefetchOnWindowFocus: true,
    socketsConfig: null,
  },
};

export default withContainer(AppContainer, {
  cache: {
    adminSettings: {},
    app: {
      initialData: {
        isRealtimeOffline: false, // set up legacy cache used in core/sockets
      },
    },
    currentUser: {},
    organizations: {},
    settings: {},
    user: {},
  },
});
